import * as React from "react";
import type { VizCandidate } from "@/lib/viz/types";

/**
 * The tab strip shown above a chart when the backend ships alternate
 * visualizations of the same data. Cognos's "Show related
 * visualizations" panel is the inspiration: a row of icon-only tabs
 * the user can click to flip the chart between bar / line / area /
 * scatter / table views.
 *
 * <p>Render rules — keep this component dumb on purpose so the parent
 * (VisualBlock) owns the active-index state:
 * <ul>
 *   <li>The first tab is always the primary spec — labelled "Default"
 *       in the tooltip so the user knows which one the system picked.</li>
 *   <li>Each subsequent tab corresponds to one {@link VizCandidate} in
 *       order. Backend already de-duplicated against the primary.</li>
 *   <li>Tabs are icon-only by default; full label appears in a
 *       title-tooltip on hover. Keeps the strip compact at low widths
 *       (Cognos does the same in their related-viz panel).</li>
 * </ul>
 *
 * <p>If there are zero candidates the tab strip should not render at
 * all — VisualBlock decides whether to mount this. A one-tab strip
 * (primary only, no candidates) adds noise without value.
 */

export interface VizTabStripProps {
  /** Primary tab — corresponds to the LLM-picked spec. */
  primary: { vizType: string; label: string; iconHint: string };
  /** Alternate viz candidates emitted by the backend generator. */
  candidates: VizCandidate[];
  /** Currently-active tab index. 0 = primary, 1..N = candidates. */
  activeIndex: number;
  /** Click handler — receives the new active index. */
  onSelect: (index: number) => void;
}

export const VizTabStrip: React.FC<VizTabStripProps> = ({
  primary, candidates, activeIndex, onSelect,
}) => {
  // Build a unified entries list so the rendering loop is one map call
  // rather than two. The primary's iconHint comes from the LLM-picked
  // vizType (e.g. "bar"); falls back to the literal string.
  const entries = [
    { iconHint: primary.iconHint || primary.vizType, label: primary.label || "Default" },
    ...candidates.map((c) => ({ iconHint: c.iconHint || c.vizType, label: c.label })),
  ];

  return (
    <div
      role="tablist"
      aria-label="Alternate visualizations"
      className="flex items-center gap-1 border-b border-zinc-200 bg-zinc-50 px-2 py-1"
    >
      {entries.map((entry, i) => {
        const active = i === activeIndex;
        return (
          <button
            key={i}
            role="tab"
            aria-selected={active}
            aria-label={entry.label}
            title={entry.label}
            onClick={() => onSelect(i)}
            className={
              "inline-flex h-7 w-7 items-center justify-center rounded text-zinc-600 " +
              "hover:bg-zinc-200 hover:text-zinc-900 focus:outline-none " +
              "focus-visible:ring-2 focus-visible:ring-datumcraft-500 " +
              (active
                ? "bg-white text-zinc-900 shadow-sm ring-1 ring-zinc-300"
                : "")
            }
          >
            <VizTypeIcon hint={entry.iconHint} />
          </button>
        );
      })}
    </div>
  );
};

/**
 * Inline SVG icons keyed by {@code iconHint}. Inline keeps the bundle
 * footprint zero and lets each icon be styled by Tailwind (currentColor).
 * Unknown hints fall through to a generic "chart" glyph rather than
 * blanking the tab, so a future backend-emitted iconHint doesn't break
 * the row.
 */
const VizTypeIcon: React.FC<{ hint: string }> = ({ hint }) => {
  const props = {
    width: 16, height: 16, viewBox: "0 0 16 16", fill: "none",
    stroke: "currentColor", strokeWidth: 1.5,
    strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
  };
  switch ((hint || "").toLowerCase()) {
    case "bar":
    case "column":
      return (
        <svg {...props}><rect x="2" y="8"  width="2.5" height="6" /><rect x="6.5" y="5"  width="2.5" height="9" /><rect x="11" y="3"  width="2.5" height="11" /></svg>
      );
    case "line":
      return (
        <svg {...props}><polyline points="2,12 6,7 9,9 14,3" /></svg>
      );
    case "area":
      return (
        <svg {...props}><polyline points="2,12 6,7 9,9 14,4" /><polygon points="2,12 6,7 9,9 14,4 14,14 2,14" fill="currentColor" fillOpacity="0.15" /></svg>
      );
    case "scatter":
    case "point":
      return (
        <svg {...props}><circle cx="3.5" cy="11" r="1.2" /><circle cx="7"   cy="6"  r="1.2" /><circle cx="10.5" cy="9"  r="1.2" /><circle cx="13"  cy="4"  r="1.2" /></svg>
      );
    case "table":
      return (
        <svg {...props}><rect x="2" y="3" width="12" height="10" rx="0.5" /><line x1="2" y1="6.5" x2="14" y2="6.5" /><line x1="8" y1="3"   x2="8"  y2="13"  /></svg>
      );
    default:
      // Generic "chart" — three bars at decreasing heights, indicates
      // a viz exists but its type is unknown to the icon library.
      return (
        <svg {...props}><line x1="3" y1="13" x2="3"  y2="6"  /><line x1="8" y1="13" x2="8"  y2="3"  /><line x1="13" y1="13" x2="13" y2="9"  /></svg>
      );
  }
};
